import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, HostBinding, HostListener, inject, Input } from '@angular/core';
import { DocumentHostComponent } from '@components/element-widgets/document-host/document-host.component';
import { ElementErrorComponent } from '@components/element-widgets/element-error/element-error.component';
import { ElementHostComponent } from '@components/element-widgets/element-host/element-host.component';
import { ElementHostOptions } from '@components/element-widgets/element-host/element-host.options';
import { FondsportraitHeaderComponent } from '@components/fondsportrait/fondsportrait-header/fondsportrait-header.component';
import {
  barChartTransformer,
  valueDevelopmentSimpleColumnOptionsTransformer,
  valueDevelopmentSimpleLinieOptionsTransformer,
} from '@utils/charts/chart-transformers';
import { isBarChart, isColumnChart, isLineChart } from '@utils/charts/chart-type-detection';
import { HighchartsSizeStrategy } from '@utils/charts/highcharts.directive';
import { CustomDomEvents } from '@utils/events/custom-events';
import { ElementPipe } from 'src/app/element-handling/element.pipe';
import { ProvideElementsDirective } from 'src/app/element-handling/provide-elements.directive';
import { FondsportraitBlueprintType } from 'src/openapi-client';
import { CountryChartComponent } from '../country-chart/country-chart.component';
import { RouteSelectedFund } from '../selected-fund';
import { ValueDevelopmentChartComponent } from '../value-development-chart/value-development-chart.component';
import { ValueDevelopmentTableComponent } from '../value-development-table/value-development-table.component';

const elementOptions: ElementHostOptions = {
  chartOptionsTransformer: function (options: Highcharts.Options): Highcharts.Options {
    if (isLineChart(options)) {
      return valueDevelopmentSimpleLinieOptionsTransformer(options);
    }

    if (isColumnChart(options)) {
      return valueDevelopmentSimpleColumnOptionsTransformer(options);
    }

    if (isBarChart(options)) {
      return barChartTransformer(options);
    }

    return options;
  },
  chartConstructerType: function (options: Highcharts.Options) {
    return isLineChart(options) ? 'stockChart' : 'chart';
  },

  chartCssClasses: function (options) {
    if (isLineChart(options)) {
      return 'stockgraph stockgraph-no-main-lines';
    }

    if (isBarChart(options)) {
      return 'barchart chart-data-labels stockgraph-no-main-lines';
    }

    if (isColumnChart(options)) {
      return 'columnchart chart-data-labels stockgraph-no-main-lines';
    }

    return '';
  },
};

@Component({
  selector: 'wct-fundsportrait-type-mapper',
  standalone: true,
  imports: [
    DocumentHostComponent,
    FondsportraitHeaderComponent,
    ValueDevelopmentTableComponent,
    ValueDevelopmentChartComponent,
    CountryChartComponent,
    ElementHostComponent,
    ElementErrorComponent,

    ProvideElementsDirective,

    ElementPipe,
    AsyncPipe,
  ],
  templateUrl: './fundsportrait-type-mapper.component.html',
  styleUrl: './fundsportrait-type-mapper.component.scss',
})
export class FundsportraitTypeMapperComponent {
  @Input({ required: true }) public documentId!: string;
  @Input({ required: true }) public reportIndex!: number;
  @Input({ required: true }) public type!: FondsportraitBlueprintType;

  @HostBinding('class.has-errors') public get hasErrors(): boolean {
    return !!this.error;
  }

  protected readonly FundsportraitBlueprintType = FondsportraitBlueprintType;
  protected readonly HighchartsSizeStrategy = HighchartsSizeStrategy;

  protected readonly selectedFund = inject(RouteSelectedFund);
  protected readonly elementOptions = elementOptions;

  protected error: Error | undefined = undefined;

  private readonly elref: ElementRef<HTMLElement> = inject(ElementRef);

  @HostListener(CustomDomEvents.ELEMENT_LOAD_ERROR, ['$event'])
  public onLoadError(event: CustomEvent<Error>): void {
    this.error = event.detail;
    event.stopPropagation();
  }
}
